/* STORY-001 — create an account.
 *
 * The screen is the prototype's, unchanged in structure: brand, one card, three
 * fields, the agreement sentence, one full-width button, and the "already have
 * an account" line beneath the card.
 */

import { PASSWORD_MIN_LENGTH, signUpRequestSchema } from '@eventuley/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import { ApiError, NetworkError } from '../../../shared/api/client.js';
import { useTranslate } from '../../../shared/i18n/index.js';
import { AuthScreen, Button, Callout, TextField } from '../../../shared/ui/index.js';
import { useLegalIndex } from '../../legal/index.js';
import { useSignUp } from '../api/index.js';

/* The contract's schema, minus the two acceptance flags the form does not ask
 * for as fields. Derived rather than re-declared: a rule written twice is a
 * rule that will disagree with itself, and the server's copy is the one that
 * matters. */
const formSchema = signUpRequestSchema.omit({ acceptsTerms: true, acceptsDpa: true });
type FormValues = z.infer<typeof formSchema>;

export function SignUpScreen() {
  const t = useTranslate();
  const navigate = useNavigate();
  const signUp = useSignUp();
  /* Both, not either: the sentence promises acceptance of two documents, so it
   * may only appear when both can be read. */
  const legalIndex = useLegalIndex();
  const published =
    legalIndex.data?.published.includes(`terms`) === true &&
    legalIndex.data?.published.includes(`dpa`) === true;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    /* Validate on the way out of a field rather than on every keystroke.
     * Telling somebody their email is invalid while they are still typing the
     * domain is technically true and unhelpful. */
    mode: 'onBlur',
    defaultValues: { email: '', displayName: '', password: '' },
  });

  /* `mutate` with a callback rather than `mutateAsync`: the latter rejects on
   * failure, and although the failure is handled — rendered from
   * `signUp.error` — the rejection itself goes unhandled. A `try/catch` with an
   * empty `catch` would do the same job while reading exactly like the
   * swallowed error `coding.md` forbids. */
  const onSubmit = handleSubmit((values) => {
    signUp.mutate(values, {
      onSuccess: () => {
        /* The response is identical whether or not the address was already
         * registered, so this screen cannot know and must not try. Both go to
         * the same place. */
        void navigate({ to: '/verify', search: { email: values.email } });
      },
    });
  });

  return (
    <AuthScreen
      title={t('auth.sign_up.title')}
      brand={t('app.name')}
      footer={
        <>
          {t('auth.sign_up.have_account')}{' '}
          <Link to="/sign-in">{t('auth.sign_up.sign_in_link')}</Link>
        </>
      }
    >
      {signUp.isError ? <SubmitError error={signUp.error} /> : null}

      {/* `noValidate` so the browser's own bubbles do not pre-empt ours. Theirs
          are not translated, not styled, and not announced consistently. */}
      <form onSubmit={onSubmit} noValidate>
        <TextField
          label={t('auth.sign_up.email_label')}
          type="email"
          autoComplete="email"
          error={errors.email ? t('error.email_invalid') : undefined}
          {...register('email')}
        />

        <TextField
          label={t('auth.sign_up.name_label')}
          autoComplete="name"
          error={errors.displayName ? t('error.required') : undefined}
          {...register('displayName')}
        />

        <TextField
          label={t('auth.sign_up.password_label')}
          type="password"
          autoComplete="new-password"
          /* Stated before submission, not discovered after — STORY-001's
              accessibility criterion. */
          hint={t('auth.sign_up.password_hint')}
          minLength={PASSWORD_MIN_LENGTH}
          error={errors.password ? t('error.password_too_short') : undefined}
          {...register('password')}
        />

        {/* Acceptance is the act of continuing, and the sentence says so. This
            is the prototype's decision and it is a contract basis rather than
            consent — consent would need an unticked box.

            Two states, chosen by whether the documents are actually published —
            STORY-048. The API writes no agreement row while the versions carry
            the `unpublished-` sentinel, so promising acceptance in that state
            would be a claim the database does not support. That exact mismatch
            was a real defect, fixed in #71, and this check is what stops it
            returning now that publication is possible.

            Fails closed: an unreadable index shows the weaker sentence. */}
        {published ? (
          <>
            <p className="field__hint">{t('auth.sign_up.agreement_published')}</p>
            {/* One link per line, with no separator character between them. A
                literal bullet would be a hardcoded user-facing string, which is
                what the pseudo-locale sweep exists to catch — and it did. */}
            <p className="field__hint">
              <Link to="/legal/$document" params={{ document: 'terms' }}>
                {t('auth.sign_up.read_terms')}
              </Link>
            </p>
            <p className="field__hint">
              <Link to="/legal/$document" params={{ document: 'dpa' }}>
                {t('auth.sign_up.read_dpa')}
              </Link>
            </p>
          </>
        ) : (
          <p className="field__hint">{t('auth.sign_up.agreement')}</p>
        )}

        <Button type="submit" block busy={isSubmitting || signUp.isPending}>
          {isSubmitting || signUp.isPending ? t('common.submitting') : t('auth.sign_up.submit')}
        </Button>
      </form>
    </AuthScreen>
  );
}

/** Turns a failure into a sentence in the reader's language. */
function SubmitError({ error }: { readonly error: unknown }) {
  const t = useTranslate();

  /* Branching on the CODE, never on the message. The message is English, is for
   * humans, and is never parsed — `coding.md`. */
  if (error instanceof ApiError && error.code === 'rate_limited') {
    return <Callout tone="warning">{t('error.rate_limited')}</Callout>;
  }
  if (error instanceof NetworkError) {
    return <Callout tone="danger">{t('error.network')}</Callout>;
  }
  return <Callout tone="danger">{t('error.unexpected')}</Callout>;
}
